import { TIER_LABEL } from '@/pages/SubscriptionSettingsPage/subscription-display';
import { SEAT_POOL_LABEL } from '@/pages/SubscriptionSettingsPage/vessel-seats';
import type { SubscriptionTier } from '@/store/subscriptions/subscriptions.types';

import type { GateReason, PermissionGates } from './permission-gates';

/**
 * Why a surface needing these permissions is locked, or `null` when nothing gates it.
 *
 * When the plan and the seat both stand in the way, the plan is the reason: moving the person
 * to a management seat alone would leave the feature locked.
 */
export const lockReasonOf = (
  gates: PermissionGates,
  names: readonly string[],
): GateReason | null => {
  const reasons = names.map((name) => gates.reasonFor(name));
  if (reasons.includes('plan')) return 'plan';
  return reasons.includes('seat') ? 'seat' : null;
};

/** The tiers ready for a sentence: `Command`, `Navigate or Command`; empty for none. */
export const plansLabel = (tiers: readonly SubscriptionTier[]): string =>
  tiers.map((tier) => TIER_LABEL[tier]).join(' or ');

export interface LockCopyInput {
  reason: GateReason;
  /** What is locked, as the section names itself: `Manage`. */
  feature: string;
  /** The plan the vessel is on: `Harbor`. */
  currentPlan: string;
  /** The plans that carry the feature, from `plansLabel`; empty when none does. */
  requiredPlan: string;
  /** A lifetime plan an admin granted, which cannot be upgraded. */
  planGranted?: boolean;
  /**
   * Whose seat a seat lock is about: the reader's own, or that of the person whose
   * permissions the reader is editing.
   */
  seatOf?: 'self' | 'member';
}

export interface LockCopy {
  /** For a pill, a settings row or the sidebar tooltip. */
  short: string;
  /** For the locked page body or the settings row's tooltip. */
  full: string;
  /** The one action that lifts the lock; `null` when the reader has none. */
  action: string | null;
}

const MANAGEMENT = SEAT_POOL_LABEL.management;
const CREW = SEAT_POOL_LABEL.crew;

/**
 * Everything a lock says, in one place, so the locked page, the sidebar and the permissions
 * settings cannot drift apart. A seat lock never offers an upgrade: a bigger plan would not
 * change the seat.
 */
export const lockCopy = ({
  reason,
  feature,
  currentPlan,
  requiredPlan,
  planGranted = false,
  seatOf = 'self',
}: LockCopyInput): LockCopy => {
  if (reason === 'seat') {
    const short = `${MANAGEMENT} seat required`;
    return seatOf === 'member'
      ? {
          short,
          full: `${feature} isn’t available with a ${CREW} seat. Move this person to a ${MANAGEMENT} seat to give them access.`,
          action: 'Manage Seats',
        }
      : {
          short,
          full: `${feature} isn’t available with your ${CREW} seat. Ask your account admin to switch you to a ${MANAGEMENT} seat and you’ll have access straight away.`,
          action: null,
        };
  }

  if (planGranted) {
    return {
      short: 'Not on your plan',
      full: `${feature} isn’t included in your lifetime plan, and a lifetime plan can’t be upgraded.`,
      action: null,
    };
  }

  // No plan carries it, so an upgrade would not help and is not offered.
  if (!requiredPlan) {
    return {
      short: 'Not on your plan',
      full: `${feature} isn’t included in your ${currentPlan} plan.`,
      action: null,
    };
  }

  return {
    short: `Available on ${requiredPlan}`,
    full: `${feature} isn’t included in your ${currentPlan} plan. It’s available on ${requiredPlan}, so upgrading gives you access right away.`,
    action: 'Compare Plans',
  };
};
